// ════════════════════════════════════════════════════════════════════
// NoteTemplatesManager
// CRUD UI for the note_templates table. Lives inside the
// "Patient Paperwork" hub, alongside Consents / Pre-Post / Questionnaires.
// Categories are free-text but the picker UI groups by them.
// ════════════════════════════════════════════════════════════════════
import { useState, useEffect } from "react";
import { supabase } from "./supabase";

const C = {
  bg: "#f8f5f0", surface: "#ffffff", card: "#ffffff", border: "#e8e4df",
  borderGold: "rgba(180,140,60,0.3)", goldBg: "rgba(180,140,60,0.08)",
  gold: "#b48c3c", goldD: "#8a6f2e",
  green: "#2e8b57", greenL: "rgba(46,139,87,0.12)",
  red: "#c0392b", redL: "rgba(192,57,43,0.10)",
  text: "#1a1a2e", muted: "#6b7280", dim: "#9ca3af", light: "#f3f4f6",
};
const F = "'Montserrat',sans-serif";
const S = "'Playfair Display','Cormorant Garamond',Georgia,serif";

// Suggested categories — owner can type any free text but these
// match the seed groupings so the dropdown stays clean.
const CATEGORY_SUGGESTIONS = [
  "Injectables / Fillers",
  "Lasers",
  "RF / Microneedling",
  "Chemical Peels",
  "Regenerative / Boosters",
  "Weight Loss",
  "Exams & Consults",
  "Other",
];

export default function NoteTemplatesManager() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [editing, setEditing] = useState(null); // {id?, category, name, body, display_order, active}

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase.from("note_templates")
      .select("*").order("category").order("display_order").order("name");
    if (!error) setItems(data || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const blankRow = () => ({ category: "Injectables / Fillers", name: "", body: "", display_order: 0, active: true });

  const save = async () => {
    if (!editing) return;
    if (!editing.name.trim() || !editing.body.trim()) { alert("Name and body required"); return; }
    const payload = {
      category: editing.category.trim() || "Other",
      name: editing.name.trim(),
      body: editing.body,
      display_order: Number(editing.display_order) || 0,
      active: editing.active !== false,
    };
    if (editing.id) {
      await supabase.from("note_templates").update(payload).eq("id", editing.id);
    } else {
      await supabase.from("note_templates").insert(payload);
    }
    setEditing(null);
    await load();
  };
  const remove = async (row) => {
    if (!window.confirm(`Delete template "${row.name}"? This cannot be undone.`)) return;
    await supabase.from("note_templates").delete().eq("id", row.id);
    await load();
  };
  const toggleActive = async (row) => {
    await supabase.from("note_templates").update({ active: !row.active }).eq("id", row.id);
    await load();
  };

  const allCategories = Array.from(new Set(items.map(i => i.category))).sort();
  const filtered = items.filter(i => {
    if (filterCategory !== "all" && i.category !== filterCategory) return false;
    if (search) {
      const q = search.toLowerCase();
      return (i.name || "").toLowerCase().includes(q) ||
             (i.category || "").toLowerCase().includes(q) ||
             (i.body || "").toLowerCase().includes(q);
    }
    return true;
  });
  // Group by category for the list display
  const grouped = {};
  for (const r of filtered) (grouped[r.category] ||= []).push(r);

  return (
    <div style={{display: "flex", flexDirection: "column", gap: 14}}>
      <div style={{display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap"}}>
        <div>
          <div style={{fontSize: 18, fontWeight: 800, color: C.text, fontFamily: S}}>📄 Clinical Note Templates</div>
          <div style={{fontSize: 12, color: C.muted, marginTop: 4}}>Reusable text snippets staff insert into Treatment Record → Clinical Notes. {items.length} templates · {allCategories.length} categories</div>
        </div>
        <button onClick={() => setEditing(blankRow())}
          style={{padding: "8px 16px", background: C.gold, color: "#fff", border: "none", borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: F}}>
          + New Template
        </button>
      </div>

      <div style={{display: "flex", gap: 8, flexWrap: "wrap"}}>
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search by name or body…"
          style={{flex: "1 1 240px", padding: "8px 12px", background: C.surface, border: "1px solid " + C.border, borderRadius: 7, fontSize: 13, fontFamily: F}}/>
        <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}
          style={{padding: "8px 12px", background: C.surface, border: "1px solid " + C.border, borderRadius: 7, fontSize: 13, fontFamily: F}}>
          <option value="all">All categories</option>
          {allCategories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {loading && <div style={{padding: 30, textAlign: "center", color: C.muted}}>Loading…</div>}

      {!loading && Object.keys(grouped).length === 0 && (
        <div style={{padding: 30, textAlign: "center", color: C.dim, background: C.light, borderRadius: 9, border: `1px dashed ${C.border}`}}>
          No templates match. {search && "Try a different search."}
        </div>
      )}

      {Object.entries(grouped).map(([cat, list]) => (
        <div key={cat} style={{display: "flex", flexDirection: "column", gap: 6}}>
          <div style={{fontSize: 11, fontWeight: 800, color: C.gold, textTransform: "uppercase", letterSpacing: "0.08em", padding: "6px 4px"}}>{cat} · {list.length}</div>
          {list.map(t => (
            <div key={t.id} style={{display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", background: t.active ? C.card : C.light, border: `1px solid ${t.active ? C.border : C.dim + "40"}`, borderRadius: 8, opacity: t.active ? 1 : 0.6}}>
              <div style={{flex: 1, minWidth: 0}}>
                <div style={{fontSize: 13, fontWeight: 700, color: C.text}}>{t.name}{!t.active && <span style={{marginLeft: 8, fontSize: 9, padding: "2px 6px", background: C.dim + "33", color: C.muted, borderRadius: 4}}>INACTIVE</span>}</div>
                <div style={{fontSize: 11, color: C.muted, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>{(t.body || "").replace(/\s+/g, " ").slice(0, 110)}…</div>
              </div>
              <button onClick={() => toggleActive(t)} title={t.active ? "Hide from picker" : "Show in picker"}
                style={{padding: "6px 10px", background: t.active ? "#fff" : C.green, color: t.active ? C.muted : "#fff", border: "1px solid " + (t.active ? C.border : C.green), borderRadius: 6, fontSize: 10, fontWeight: 700, cursor: "pointer"}}>
                {t.active ? "Hide" : "Show"}
              </button>
              <button onClick={() => setEditing({...t})}
                style={{padding: "6px 12px", background: C.goldBg, color: C.goldD, border: "1px solid " + C.borderGold, borderRadius: 6, fontSize: 10, fontWeight: 700, cursor: "pointer"}}>Edit</button>
              <button onClick={() => remove(t)}
                style={{padding: "6px 10px", background: C.redL, color: C.red, border: "1px solid " + C.red + "40", borderRadius: 6, fontSize: 10, fontWeight: 700, cursor: "pointer"}}>🗑</button>
            </div>
          ))}
        </div>
      ))}

      {editing && (
        <div onClick={() => setEditing(null)} style={{position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20}}>
          <div onClick={e => e.stopPropagation()} style={{background: C.bg, width: "100%", maxWidth: 720, maxHeight: "90vh", overflow: "auto", borderRadius: 14, boxShadow: "0 18px 60px rgba(0,0,0,0.4)"}}>
            <div style={{padding: "16px 22px", borderBottom: `1px solid ${C.border}`, background: C.goldBg, display: "flex", alignItems: "center", justifyContent: "space-between"}}>
              <div style={{fontSize: 16, fontWeight: 800, color: C.text, fontFamily: S}}>{editing.id ? "Edit Template" : "New Template"}</div>
              <button onClick={() => setEditing(null)} style={{background: "transparent", border: "none", fontSize: 22, color: C.muted, cursor: "pointer"}}>×</button>
            </div>
            <div style={{padding: 22, display: "flex", flexDirection: "column", gap: 14}}>
              <div>
                <label style={{fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em"}}>Category</label>
                <input value={editing.category} onChange={e => setEditing({...editing, category: e.target.value})} list="cat-suggest"
                  style={{width: "100%", padding: "10px 12px", marginTop: 4, background: C.surface, border: "1px solid " + C.border, borderRadius: 7, fontSize: 13, fontFamily: F, boxSizing: "border-box"}}/>
                <datalist id="cat-suggest">
                  {CATEGORY_SUGGESTIONS.map(c => <option key={c} value={c}/>)}
                </datalist>
              </div>
              <div>
                <label style={{fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em"}}>Template Name</label>
                <input value={editing.name} onChange={e => setEditing({...editing, name: e.target.value})}
                  placeholder="e.g. Botox Follow-up"
                  style={{width: "100%", padding: "10px 12px", marginTop: 4, background: C.surface, border: "1px solid " + C.border, borderRadius: 7, fontSize: 13, fontFamily: F, boxSizing: "border-box"}}/>
              </div>
              <div>
                <label style={{fontSize: 11, color: C.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em"}}>Body (template text)</label>
                <textarea value={editing.body} onChange={e => setEditing({...editing, body: e.target.value})}
                  rows={16}
                  placeholder="The full template text. Use ___ as blanks for the provider to fill in at the time of treatment."
                  style={{width: "100%", padding: "10px 12px", marginTop: 4, background: C.surface, border: "1px solid " + C.border, borderRadius: 7, fontSize: 12, fontFamily: "Menlo, Consolas, monospace", lineHeight: 1.55, boxSizing: "border-box", resize: "vertical"}}/>
              </div>
              <div style={{display: "flex", gap: 14, alignItems: "center"}}>
                <label style={{fontSize: 12, color: C.text, display: "flex", alignItems: "center", gap: 6}}>
                  <input type="checkbox" checked={editing.active !== false} onChange={e => setEditing({...editing, active: e.target.checked})}/>
                  Active (show in picker)
                </label>
                <label style={{fontSize: 11, color: C.muted, display: "flex", alignItems: "center", gap: 6}}>
                  Sort order:
                  <input type="number" value={editing.display_order ?? 0} onChange={e => setEditing({...editing, display_order: Number(e.target.value)})}
                    style={{width: 64, padding: "6px 8px", background: C.surface, border: "1px solid " + C.border, borderRadius: 6, fontSize: 12, fontFamily: F}}/>
                </label>
              </div>
            </div>
            <div style={{padding: "12px 22px", borderTop: `1px solid ${C.border}`, display: "flex", justifyContent: "flex-end", gap: 8}}>
              <button onClick={() => setEditing(null)} style={{padding: "9px 18px", background: "#fff", color: C.muted, border: "1px solid " + C.border, borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: F}}>Cancel</button>
              <button onClick={save} style={{padding: "9px 18px", background: C.gold, color: "#fff", border: "none", borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: "pointer", fontFamily: F}}>{editing.id ? "Save Changes" : "Create Template"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
